import { put, takeLatest, select } from 'redux-saga/effects'
import * as Eff from 'redux-saga/effects' // tslint:disable-line:no-duplicate-imports
import toastr from 'toastr'
import 'toastr/build/toastr.css'

import Logger from '@Utils/Logger'
import { IAction, ISuccessResponse } from '@Root/types'

import {
  fetchTermbases,
  fetchTermbasesSuccess,
  fetchTermbasesFailure,
  fetchTermbaseEntries,
  fetchTermbaseEntriesSuccess,
  fetchTermbaseEntriesFailure,
  createTermbaseSuccess,
  createTermbaseFailure,
  updateTermbaseSuccess,
  updateTermbaseFailure,
  deleteTermbaseSuccess,
  deleteTermbaseFailure,
  updateTermbaseEntrySuccess,
  updateTermbaseEntryFailure,
  addTermbaseEntrySuccess,
  addTermbaseEntryFailure,
  deleteTermbaseEntrySuccess,
  deleteTermbaseEntryFailure,
  importTermbaseEntriesSuccess,
  importTermbaseEntriesFailure,
} from './actions'

import {
  closeImportTermsPopup
} from '@SiteModules/Popup/actions'

import {
  TermbaseActionTypes,
  IFetchTermbasesSuccessResponseBody,
  IFetchTermbaseEntriesSuccessResponseBody,
  IFetchTermbaseEntriesPayload,
  ICreateTermbasePayload,
  IUpdateTermbasePayload,
  IDeleteTermbasePayload,
  IUpdateTermbaseEntryPayload,
  IDeleteTermbaseEntryPayload,
  IFetchTermbasesPayload,
  IImportTermbaseEntriesPayload,
} from './types'

import * as API from './api'

const call: any = Eff.call

function* fetchTermbasesSaga(action: IAction<IFetchTermbasesPayload>) {
  try {
    const response: ISuccessResponse<IFetchTermbasesSuccessResponseBody> = yield call(API.fetchTermbases, action.payload)
    const payloadResponse = response.data
    yield put(fetchTermbasesSuccess(payloadResponse))
  } catch (e) {
    Logger.error(e, 'Term bases could not be loaded')
    yield put(fetchTermbasesFailure())
  }
}

function* fetchTermbaseEntriesSaga(action: IAction<IFetchTermbaseEntriesPayload>) {
  try {
    const response: ISuccessResponse<IFetchTermbaseEntriesSuccessResponseBody> = yield call(API.fetchTermbaseEntries, action.payload)
    const payloadResponse = response.data
    yield put(fetchTermbaseEntriesSuccess(payloadResponse))
  } catch (e) {
    Logger.error(e, 'Terms could not be loaded')
    yield put(fetchTermbaseEntriesFailure())
  }
}

function* createTermbaseSaga(action: IAction<ICreateTermbasePayload>) {
  try {
    const { payload } = action
    yield call(API.createTermbase, payload)
    yield put(createTermbaseSuccess())
    toastr.success('', 'Term base created')
    yield put(fetchTermbases({ workspaceId: payload.workspaceId }))
  } catch (e) {
    Logger.error(e, 'Term base could not be created')
    yield put(createTermbaseFailure())
  }
}

function* updateTermbaseSaga(action: IAction<IUpdateTermbasePayload>): Generator<any, any, any> {
  try {
    yield call(API.updateTermbase, action.payload)
    yield put(updateTermbaseSuccess())
    toastr.success('', 'Changes saved')
    const state = yield select()
    yield put(fetchTermbases({ workspaceId: state.workspaces.currentWorkspaceId }))
  } catch (e) {
    Logger.error(e, 'Changes could not be saved')
    yield put(updateTermbaseFailure())
  }
}

function* deleteTermbaseSaga(action: IAction<IDeleteTermbasePayload>): Generator<any, any, any> {
  try {
    yield call(API.deleteTermbase, action.payload)
    yield put(deleteTermbaseSuccess())
    toastr.success('', 'Term base deleted')
    const state = yield select()
    yield put(fetchTermbases({ workspaceId: state.workspaces.currentWorkspaceId }))
  } catch (e) {
    Logger.error(e, 'Term base could not be deleted')
    yield put(deleteTermbaseFailure())
  }
}

function* updateTermbaseEntrySaga(action: IAction<IUpdateTermbaseEntryPayload>): Generator<any, any, any> {
  try {
    yield call(API.updateTermbaseEntry, action.payload)
    yield put(updateTermbaseEntrySuccess())
    toastr.success('', 'Changes saved')

    const state = yield select()
    const fetchTermbaseEntriesPayload = getTermbaseEntriesPayload(state)

    yield put(fetchTermbaseEntries(fetchTermbaseEntriesPayload))
  } catch (e) {
    Logger.error(e, 'Changes could not be saved')
    yield put(updateTermbaseEntryFailure())
  }
}

function* addTermbaseEntrySaga(action: IAction<IUpdateTermbaseEntryPayload>): Generator<any, any, any> {
  try {
    yield call(API.addTermbaseEntry, action.payload)
    yield put(addTermbaseEntrySuccess())
    toastr.success('', 'Term added')

    const state = yield select()
    const fetchTermbaseEntriesPayload = getTermbaseEntriesPayload(state)

    yield put(fetchTermbaseEntries(fetchTermbaseEntriesPayload))
  } catch (e) {
    Logger.error(e, 'Term could not be added')
    yield put(addTermbaseEntryFailure())
  }
}

function* deleteTermbaseEntrySaga(action: IAction<IDeleteTermbaseEntryPayload>): Generator<any, any, any> {
  try {
    yield call(API.deleteTermbaseEntry, action.payload)
    yield put(deleteTermbaseEntrySuccess())
    toastr.success('', 'Term deleted')

    const state = yield select()
    const fetchTermbaseEntriesPayload = getTermbaseEntriesPayload(state)

    yield put(fetchTermbaseEntries(fetchTermbaseEntriesPayload))
  } catch (e) {
    Logger.error(e, 'Term could not be deleted')
    yield put(deleteTermbaseEntryFailure())
  }
}

function* importTermbaseEntriesSaga(action: IAction<IImportTermbaseEntriesPayload>): Generator<any, any, any> {
  try {
    yield call(API.importTermbaseEntries, action.payload)
    yield put(importTermbaseEntriesSuccess())
    toastr.success('', 'Terms imported')

    const state = yield select()
    const fetchTermbaseEntriesPayload = getTermbaseEntriesPayload(state)

    yield put(fetchTermbaseEntries(fetchTermbaseEntriesPayload))
    yield put(closeImportTermsPopup())
  } catch (e) {
    Logger.error(e, 'Terms could not be imported')
    yield put(importTermbaseEntriesFailure())
  }
}

const getTermbaseEntriesPayload = (state: any) => {
  const { searchTerm, activeTermbaseId, activePage } = state.termbases.page
  const hasSearchTerm = searchTerm && searchTerm.length > 0
  return {
    termbaseId: activeTermbaseId,
    page: activePage,
    ...(hasSearchTerm ? { searchTerm } : {}),
  }
}

function* termbaseSagas() {
  yield takeLatest(TermbaseActionTypes.FETCH_TERMBASES, fetchTermbasesSaga)
  yield takeLatest(TermbaseActionTypes.FETCH_TERMBASE_ENTRIES, fetchTermbaseEntriesSaga)
  yield takeLatest(TermbaseActionTypes.CREATE_TERMBASE, createTermbaseSaga)
  yield takeLatest(TermbaseActionTypes.UPDATE_TERMBASE, updateTermbaseSaga)
  yield takeLatest(TermbaseActionTypes.DELETE_TERMBASE, deleteTermbaseSaga)
  yield takeLatest(TermbaseActionTypes.UPDATE_TERMBASE_ENTRY, updateTermbaseEntrySaga)
  yield takeLatest(TermbaseActionTypes.ADD_TERMBASE_ENTRY, addTermbaseEntrySaga)
  yield takeLatest(TermbaseActionTypes.DELETE_TERMBASE_ENTRY, deleteTermbaseEntrySaga)
  yield takeLatest(TermbaseActionTypes.IMPORT_TERMBASE_ENTRIES, importTermbaseEntriesSaga)
}

export default termbaseSagas
